import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ChartModule } from 'angular-highcharts';
import * as Highcharts from 'highcharts';
import HC_exporting from 'highcharts/modules/exporting';
import HC_more from 'highcharts/highcharts-more';
import HC_stock from 'highcharts/modules/stock';
import HC_map from 'highcharts/modules/map';
import HC_gantt from 'highcharts/modules/gantt';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MatNativeDateModule,
  MatRippleModule,
  NativeDateAdapter,
  NativeDateModule,
} from '@angular/material/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { CdkColumnDef } from '@angular/cdk/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTabsModule } from '@angular/material/tabs';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogTitle,
  MAT_DIALOG_DEFAULT_OPTIONS,
} from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
// Imported at root so DecimalProvider, RxFormBuilder and DecimalPipe are
// reachable from every lazy chunk and from MatDialog's platform overlay.
// Without this, RxFormControlDirective (auto-attaches to any [ngModel] /
// [formControlName] / [formControl]) fails to construct when a dialog is
// opened from a lazy-loaded module.
import { RxReactiveFormsModule } from '@rxweb/reactive-form-validators';

import { FullCalendarModule } from '@fullcalendar/angular';

import { LoginComponent } from './login/login.component';
import { ChatbotComponent } from './chatbot/chatbot.component';
import { RegistrationComponent } from './registration/registration.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { FormatCurrencyPipe } from './pipes/format-currency.pipe';
import { LoaderComponent } from './loader/loader.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { UserSwitcherComponent } from './user-switcher/user-switcher.component';
import { InvoiceButtonRendererMaintenanceComponent } from './transport/maintenance/button-invoice-renderer-maintenance.component';
import { ProfileModalComponent } from './admin/profile-modal/profile-modal.component';
import { LoaderInterceptor } from './shared/interceptors/loader.interceptor';
import { AssetAuthInterceptor } from './services/asset-auth.interceptor';
import { SharedModule } from './shared/shared.module';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { AcademicYearInterceptor } from './interceptors/academic-year';
import { JwtAuthInterceptor } from './interceptors/jwt-auth.interceptor';
import { MaintenanceAlertComponent } from './shared/components/maintenance-alert/maintenance-alert.component';
import { WhatsNewModalComponent } from './shared/components/whats-new-modal/whats-new-modal.component';

// Initialize modules
HC_exporting(Highcharts);
HC_more(Highcharts);
HC_stock(Highcharts);
HC_map(Highcharts);
HC_gantt(Highcharts);

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ChatbotComponent,
    RegistrationComponent,
    PageNotFoundComponent,
    FormatCurrencyPipe,
    LoaderComponent,
    UserSwitcherComponent,
    InvoiceButtonRendererMaintenanceComponent,
    ForgotPasswordComponent,
    MaintenanceAlertComponent,
    WhatsNewModalComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatSidenavModule,
    MatToolbarModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatRippleModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatAutocompleteModule,
    MatTabsModule,
    MatSelectModule,
    HttpClientModule,
    MatDividerModule,
    MatListModule,
    MatDialogModule,
    MatCardModule,
    MatTooltipModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    RxReactiveFormsModule,
    MatGridListModule,
    MatRadioModule,
    MatDatepickerModule,
    NativeDateModule,
    MatNativeDateModule,
    ChartModule,
    FullCalendarModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    ProfileModalComponent,
    SharedModule
  ],
  providers: [CdkColumnDef, NativeDateAdapter,
    {
      // Override Material's default maxWidth (80vw) which constrains
      // sms-dialog-lg/xl panes below their target width on narrow viewports.
      // Material applies maxWidth as an inline style which beats CSS unless
      // !important; setting it here removes the constraint at the source so
      // sms-dialog-lg (850px) and -xl (1100px) reach their full width.
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { maxWidth: '95vw' },
    },
    {
      // Patch MatDialog.prototype.open so EVERY instance (including those in
      // lazy-loaded modules) blurs the active element before opening. This
      // prevents the browser "Blocked aria-hidden" warning, which fires when
      // Material sets aria-hidden on app-root while a descendant still has
      // focus. Prototype patching covers lazy instances; instance patching
      // (the previous attempt) only covered the root injector's instance.
      provide: APP_INITIALIZER,
      useFactory: (dialog: MatDialog) => () => {
        const proto = Object.getPrototypeOf(dialog);
        const orig = proto.open;
        proto.open = function(this: MatDialog, ...args: Parameters<typeof dialog.open>) {
          (document.activeElement as HTMLElement)?.blur();
          return orig.apply(this, args);
        };
      },
      deps: [MatDialog],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AssetAuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtAuthInterceptor,
      multi: true
    },
  //  {
  //     provide: HTTP_INTERCEPTORS,
  //     useClass: AcademicYearInterceptor,
  //     multi: true
  //   }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
